import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import { EssentialItemActionSheetSaveButton } from './essential-item-action-sheet-save-button';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import {
  SCREEN_PADDING,
  TYPOGRAPHY,
  UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH,
} from '@/styles';
import {
  SkuListOption,
  UpdateOrderGuideItemSkuList,
} from './update-order-guide-item-sku-list';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface Props {
  itemMetadata: ItemListOptionMetadata;
  onClose: () => void;
}

export const EssentialItemActionSheet: React.FC<Props> = ({
  onClose,
  itemMetadata,
}) => {
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const { isSmallLayout } = useLayoutContext();
  const { height } = useWindowDimensions();

  const maxHeight = Math.floor(height * 0.85);
  const renderSingleSku = itemMetadata.skus.length <= 1;

  const [selectedVariantPackUUIDs, setSelectedVariantPackUUIDs] = useState<
    Set<string>
  >(() => {
    if (renderSingleSku) {
      // if only one item, default to opposite value
      const isEssential = essentialVariantPackUUIDs.has(
        itemMetadata.variantPackUUID,
      );
      return isEssential ? new Set() : new Set([itemMetadata.variantPackUUID]);
    }

    // otherwise, set to current values
    return new Set(
      itemMetadata.skus.reduce<string[]>((acc, sku) => {
        const currentEssential = essentialVariantPackUUIDs.has(
          sku.variantPackUUID,
        );
        if (currentEssential) {
          acc.push(sku.variantPackUUID);
        }
        return acc;
      }, []),
    );
  });

  const onRowSelect = (option: SkuListOption) => {
    setSelectedVariantPackUUIDs((prev) => {
      const variantPackUUID = String(option.value);
      return prev.has(variantPackUUID)
        ? new Set([...prev].filter((uuid) => uuid !== variantPackUUID))
        : new Set([...prev, variantPackUUID]);
    });
  };

  const title = renderSingleSku
    ? i18n.t('essential_item')
    : i18n.t('select_essential_items');

  return (
    <BaseActionSheet
      contentStyle={[styles.base, { maxHeight }]}
      isVisible
      modalStyle={isSmallLayout ? null : styles.modalLarge}
      onClose={onClose}
      title={title}
    >
      {isSmallLayout ? (
        <Text style={[TYPOGRAPHY.h2, styles.title]}>{title}</Text>
      ) : null}
      {renderSingleSku ? (
        <Text style={[TYPOGRAPHY.small, styles.descriptionSingle]}>
          {selectedVariantPackUUIDs.size === 0
            ? i18n.t('essential_item_confirmation.remove_message')
            : i18n.t('essential_item_confirmation.add_message')}
        </Text>
      ) : (
        <>
          <Text style={[TYPOGRAPHY.small, styles.description]}>
            {i18n.t('essential_item_confirmation.toggle_message')}
          </Text>
          <UpdateOrderGuideItemSkuList
            itemMetadata={itemMetadata}
            onSelect={onRowSelect}
            renderLeftComponent={(option) => {
              const isSelected = selectedVariantPackUUIDs.has(
                String(option.value),
              );

              return (
                <Checkbox
                  isSelected={isSelected}
                  onSelect={() => {
                    onRowSelect(option);
                  }}
                  style={styles.checkboxPadding}
                  trackingData={{
                    name: 'essential_item_sheet.sku_list_checkbox',
                    properties: {
                      checked: !isSelected,
                      variantPackUUID: String(option.value),
                    },
                  }}
                />
              );
            }}
          />
        </>
      )}
      <View style={styles.buttonWrapper}>
        <EssentialItemActionSheetSaveButton
          itemMetadata={itemMetadata}
          onSaved={onClose}
          selectedVariantPackUUIDs={selectedVariantPackUUIDs}
        />
        <Button
          onPress={onClose}
          trackingData={{
            name: 'essential_item_sheet.cancel_button',
          }}
          type="tertiary"
        >
          {i18n.t('cancel')}
        </Button>
      </View>
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  base: {
    paddingHorizontal: 0,
  },
  buttonWrapper: {
    paddingHorizontal: SCREEN_PADDING,
  },
  checkboxPadding: {
    // define separately to override the individual base values
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  description: {
    marginBottom: 8,
    paddingHorizontal: SCREEN_PADDING,
  },
  descriptionSingle: {
    marginBottom: SCREEN_PADDING,
    paddingHorizontal: SCREEN_PADDING,
  },
  modalLarge: {
    maxWidth: UPDATE_ORDER_GUIDE_ACTION_SHEET_MAX_WIDTH,
  },
  title: {
    marginBottom: 8,
    paddingLeft: SCREEN_PADDING,
  },
});
